/**
 * CropModalInner
 *
 * Lives inside CropModalContextProvider. Orchestrates all hooks,
 * handles lifecycle effects, and renders the modal layout.
 */

import { useCallback, useEffect, useMemo, useState } from 'react'
import { Box, Divider, LinearProgress, Typography } from '@mui/material'
import { useOrgParams } from '~/common/use-org-params'
import { useSeasonList } from '~/farm/use-season-list'
import { ClosingAwareModal } from '~/components/closing-aware-modal'
import { useCropModalContext, useCropModalContextDispatch, loadEditCrop } from '../context/crop-modal-context'
import { calcMaxSafeSuccessions } from '../hooks/use-harvest-frost-warning'
import { useCropFormData } from '../hooks/use-crop-form-data'
import { useEffectiveSelection } from '../hooks/use-effective-selection'
import { usePreviewSchedule } from '../hooks/use-preview-schedule'
import { useCropModalHandlers } from '../hooks/use-crop-modal-handlers'
import { useCropDetails } from '~/crop/use-crop-details'
import { useCropData } from '~/crop/crop-list/use-crop-data'
import { useCropRelationships } from '~/map/use-crop-relationships'
import { CropFieldsRow } from './crop-fields-row'
import { CropScheduleSection } from './crop-schedule-section'
import { CropSuccessionSection } from './crop-succession-section'
import { CropNotesField } from './crop-notes-field'
import { CropModalFooter } from './crop-modal-footer'
import { CropBulkCancelModal } from './crop-bulk-cancel-modal'
import { DtmDisclaimerTooltip } from './dtm-disclaimer-tooltip'
import { useFeatures } from '~/common/use-features'

export const CropModalInner = () => {
  const {
    cropId, open, userSelectedOrgId, adjustedPhases,
    adjustedTasks, selectedPhaseCodes, successions, growthType, newCropsQueue,
  } = useCropModalContext()
  const dispatch = useCropModalContextDispatch()
  const { features } = useFeatures()

  // Season guard: new crops require a seasonId if the farm has seasons
  const { farmId, seasonId } = useOrgParams()
  const { seasons: farmSeasons } = useSeasonList({ farmId })
  const seasonRequired = !cropId && farmSeasons.length > 0 && !seasonId

  // Reference data
  const formData = useCropFormData()
  const {
    cropGrowthTypes, cropLifecycleTypes, cropPropagationYypes, collectionTypes,
    cropTemplates, cropArchetypes, cropFamilyTypes, climateData, activeOrgs, hierarchalOrgs,
    farmName, plotName, patchName, invalidate,
  } = formData

  // Derived selection logic
  const selection = useEffectiveSelection({
    cropPropagationYypes, cropArchetypes, cropTemplates, climateData,
  })
  const {
    propagationCodes, effectivePropagationCodes, activePropagationCode,
    availableCropPropagationTypes, cropPhaseTypes,
    effectivePhaseCodes, effectiveTaskCodes,
    sortedCropTemplates, filterSpeciesOptions, filterFamilyOptions, frostDates, effectiveTemplate,
  } = selection

  // Preview schedule generation
  const { previewScheduleData, timelineScrollConfig, defaultPhases, defaultTasks, generateTaskFromCurrentPhases } = usePreviewSchedule({
    cropPhaseTypes, effectivePhaseCodes, effectiveTaskCodes,
    effectivePropagationCodes, activePropagationCode, frostDates, effectiveTemplate,
    collectionTypes,
    farmClimate: climateData,
  })

  const maxSafeSuccessions = useMemo(() => calcMaxSafeSuccessions({
    previewScheduleData, frostDates, intervalDays: successions?.intervalDays, growthType,
  }), [previewScheduleData, frostDates, successions?.intervalDays, growthType])

  // Handlers
  const handlers = useCropModalHandlers({
    cropArchetypes, propagationCodes, effectiveTaskCodes, effectivePhaseCodes,
    cropPhaseTypes, previewScheduleData, frostDates,
    generateTaskFromCurrentPhases, defaultTasks,
  })
  const { _onClose } = handlers

  const [isSubmitting, setIsSubmitting] = useState(false)
  const wrappedOnSubmit = useCallback(async () => {
    setIsSubmitting(true)
    try {
      await handlers.onSubmit()
    } finally {
      setIsSubmitting(false)
    }
  }, [handlers])

  const [showBulkCancelConfirm, setShowBulkCancelConfirm] = useState(false)
  const handleRequestClose = useCallback(() => {
    if (newCropsQueue.length > 0) {
      setShowBulkCancelConfirm(true)
    } else {
      handlers._onClose()
    }
  }, [newCropsQueue.length, handlers])

  // Relationship data for companion filter
  const { crops } = useCropData()
  const { classifyTemplateForCrop } = useCropRelationships({ crops })

  // Edit mode: load crop data into context
  const { cropDetails: overrideCrop, loading: cropDetailsLoading } = useCropDetails({ cropId })
  useEffect(() => {
    if (!overrideCrop || !cropArchetypes.length || !cropTemplates.length) return
    const archetype = cropArchetypes.find(({ code }) => code === overrideCrop.archetype_code)
    const loadedTemplate = cropTemplates.find(t => t.id === overrideCrop.template_id)
    const count = overrideCrop.count

    // Determine if template_id points to a variety (has parent) or a species (no parent)
    let speciesTemplate, varietyTemplate
    if (loadedTemplate?.parent_template_id) {
      varietyTemplate = loadedTemplate
      speciesTemplate = cropTemplates.find(t => t.id === loadedTemplate.parent_template_id)
    } else {
      speciesTemplate = loadedTemplate
      varietyTemplate = null
    }

    dispatch(loadEditCrop({
      crop: overrideCrop,
      archetype,
      template: speciesTemplate,
      variety: varietyTemplate,
      count,
    }))
  }, [overrideCrop, cropArchetypes, cropTemplates, dispatch])

  // Invalidate data on open
  useEffect(() => {
    if (open) invalidate()
  }, [open, invalidate])

  // Org defaults
  const selectedOrgId = userSelectedOrgId ?? activeOrgs[activeOrgs?.length - 1]?.id
  const selectedOrgName = useCropModalContext().userSelectedOrgName ?? activeOrgs[activeOrgs?.length - 1]?.name ?? ''

  return (
    <>
      <ClosingAwareModal
        open={open}
        onClose={handleRequestClose}
        loading={isSubmitting}
        sx={{
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'center',
          overflowY: 'auto',
          py: 4,
        }}
      >
        <Box
          component="form"
          sx={{
            position: 'relative',
            width: { xs: '90vw', md: '80vw', lg: '70vw' },
            bgcolor: 'background.paper',
            borderRadius: '0.5rem',
            boxShadow: 24,
            p: '1rem 2rem',
            my: 'auto',
          }}
        >
          <Typography id="modal-modal-title" variant="h4">
            {cropId ? 'Edit' : 'Add'} Crop
          </Typography>
          <Typography id="modal-modal-subtitle" variant="body2">
            Create estimated schedule and task windows. You will be able to update these with task reporting.
          </Typography>

          <DtmDisclaimerTooltip />

          <Divider sx={{ pb: '0.5rem' }} variant='fullWidth' />

          {cropId && cropDetailsLoading && <LinearProgress />}

          <CropFieldsRow
            cropArchetypes={cropArchetypes}
            sortedCropTemplates={sortedCropTemplates}
            filterSpeciesOptions={filterSpeciesOptions}
            filterFamilyOptions={filterFamilyOptions}
            cropFamilyTypes={cropFamilyTypes}
            collectionTypes={collectionTypes}
            handlers={handlers}
            crops={crops}
            classifyTemplateForCrop={classifyTemplateForCrop}
            frostDates={frostDates}
            farmClimate={climateData}
          />

          <CropScheduleSection
            cropPhaseTypes={cropPhaseTypes}
            cropGrowthTypes={cropGrowthTypes}
            cropLifecycleTypes={cropLifecycleTypes}
            availableCropPropagationTypes={availableCropPropagationTypes}
            effectivePhaseCodes={effectivePhaseCodes}
            effectiveTaskCodes={effectiveTaskCodes}
            effectivePropagationCodes={effectivePropagationCodes}
            activePropagationCode={activePropagationCode}
            previewScheduleData={previewScheduleData}
            timelineScrollConfig={timelineScrollConfig}
            defaultPhases={defaultPhases}
            defaultTasks={defaultTasks}
            adjustedPhases={adjustedPhases}
            adjustedTasks={adjustedTasks}
            selectedPhaseCodes={selectedPhaseCodes}
            climateData={climateData}
            frostDates={frostDates}
            handlers={handlers}
          />

          {features['crop_successions'] && (
            <CropSuccessionSection
              onSuccessionChange={handlers.handleSuccessionChange}
              maxSafeSuccessions={maxSafeSuccessions}
            />
          )}

          <CropNotesField effectiveTaskCodes={effectiveTaskCodes} />

          <CropModalFooter
            selectedOrgId={selectedOrgId}
            selectedOrgName={selectedOrgName}
            hierarchalOrgs={hierarchalOrgs}
            farmName={farmName}
            plotName={plotName}
            patchName={patchName}
            handlers={{ ...handlers, onSubmit: wrappedOnSubmit, _onClose: handleRequestClose }}
            previewScheduleData={previewScheduleData}
            frostDates={frostDates}
            seasonRequired={seasonRequired}
          />
        </Box>
      </ClosingAwareModal>
      <CropBulkCancelModal
        open={showBulkCancelConfirm}
        queueCount={newCropsQueue.length}
        onConfirm={() => { setShowBulkCancelConfirm(false); handlers._onClose() }}
        onBack={() => setShowBulkCancelConfirm(false)}
        onSubmitQueue={async () => { setShowBulkCancelConfirm(false); await handlers.onSubmitQueueOnly() }}
      />
    </>
  )
}
